import type { Cue, CueWord } from "./cues";
import { synthesizeWords } from "./cues";
import type { CueIdFactory } from "./edit";
import { hasSpaceJunction, joinCueTexts, joinWordTexts, wordTextSpans } from "./text";

export interface CueSegmentationConfig {
  stopCharacters: string[];
  maxChars: number;
  maxDurationS: number;
  splitAfterGapS: number;
  /** Split wherever the source text separates two words with whitespace. */
  splitOnWhitespace: boolean;
}

export interface CueSegmentationOptions {
  /** Deterministic source for IDs assigned to newly-created cues. */
  createId?: CueIdFactory;
}

export const DEFAULT_SEGMENTATION_CONFIG: CueSegmentationConfig = {
  // Latin sentence-final marks plus their CJK counterparts. The CJK set also
  // includes the clause marks (，、；): no-space scripts have no other
  // phrase-boundary signal, and CJK subtitle lines conventionally break there.
  // None of these characters occur in Latin text, so the wider default is
  // inert outside CJK content.
  stopCharacters: [".", "?", "!", "。", "！", "？", "…", "，", "、", "；"],
  maxDurationS: 2,
  splitAfterGapS: 0.5,
  maxChars: 50,
  splitOnWhitespace: false,
};

interface WordSource {
  cue: Cue;
  wordIndex: number;
  word: CueWord;
  charStart?: number;
  charEnd?: number;
}

const cloneWord = (word: CueWord): CueWord => ({ ...word });

const sourcesForCue = (cue: Cue): WordSource[] => {
  const words = synthesizeWords(cue).filter((word) => word.text.trim());
  const spans = wordTextSpans(cue.text, words);
  return words.map((word, wordIndex) => ({
    cue,
    wordIndex,
    word,
    ...(spans?.[wordIndex] ?? {}),
  }));
};

const textFromSources = (sources: WordSource[]): string => {
  let text = "";
  let chunkStart = 0;
  while (chunkStart < sources.length) {
    const first = sources[chunkStart];
    if (!first) break;
    let chunkEnd = chunkStart + 1;
    while (sources[chunkEnd]?.cue === first.cue) chunkEnd += 1;
    const chunk = sources.slice(chunkStart, chunkEnd);
    const hasExactSpans = chunk.every(
      (source) => source.charStart !== undefined && source.charEnd !== undefined
    );
    let chunkText = joinWordTexts(chunk.map((source) => source.word));
    if (hasExactSpans) {
      chunkText = first.word.text.trim();
      for (let index = 1; index < chunk.length; index += 1) {
        const previous = chunk[index - 1];
        const current = chunk[index];
        if (!previous || !current) continue;
        const hadWhitespace = (current.charStart ?? 0) > (previous.charEnd ?? 0);
        chunkText += `${hadWhitespace ? " " : ""}${current.word.text.trim()}`;
      }
    }
    text = joinCueTexts(text, chunkText);
    chunkStart = chunkEnd;
  }
  return text;
};

const idForPiece = (
  source: WordSource | undefined,
  usedPreservedIds: Set<string>,
  createId: CueIdFactory | undefined
): string | undefined => {
  const id = source?.cue.id;
  if (source?.wordIndex === 0 && id && !usedPreservedIds.has(id)) {
    usedPreservedIds.add(id);
    return id;
  }
  return createId?.();
};

const cueFromSources = (
  sources: WordSource[],
  usedPreservedIds: Set<string>,
  createId: CueIdFactory | undefined
): Cue | null => {
  const first = sources[0];
  const last = sources[sources.length - 1];
  if (!first || !last) return null;

  const words = sources.map((source) => cloneWord(source.word));
  const id = idForPiece(first, usedPreservedIds, createId);
  return {
    ...(id ? { id } : {}),
    start: first.word.start,
    end: last.word.end,
    text: textFromSources(sources),
    words,
  };
};

/**
 * Explode each cue into one cue per word. Existing word timings are used when
 * present; otherwise timings are synthesized from the cue text and duration.
 */
export function oneWordPerCue(cues: Cue[], options: CueSegmentationOptions = {}): Cue[] {
  const usedPreservedIds = new Set<string>();
  return cues.flatMap((cue) =>
    sourcesForCue(cue).map((source) => {
      const { word } = source;
      const id = idForPiece(source, usedPreservedIds, options.createId);
      return {
        ...(id ? { id } : {}),
        start: word.start,
        end: word.end,
        text: word.text.trim(),
        words: [cloneWord(word)],
      };
    })
  );
}

const wouldExceedMaxChars = (buffer: WordSource[], next: WordSource, maxChars: number): boolean => {
  if (maxChars <= 0 || buffer.length === 0) return false;
  return textFromSources([...buffer, next]).length > maxChars;
};

const wouldExceedMaxDuration = (
  buffer: WordSource[],
  next: CueWord,
  maxDurationS: number
): boolean => {
  const first = buffer[0];
  if (maxDurationS <= 0 || !first) return false;
  return next.end - first.word.start > maxDurationS;
};

const hasSplitAfterGap = (
  current: CueWord,
  next: CueWord | undefined,
  splitAfterGapS: number
): boolean => splitAfterGapS > 0 && !!next && next.start - current.end > splitAfterGapS;

const endsWithStopCharacter = (word: CueWord, stopCharacters: string[]): boolean => {
  const trimmed = word.text.trim();
  const last = trimmed.charAt(trimmed.length - 1);
  return !!last && stopCharacters.includes(last);
};

/** Whether the flattened text separates these two words with whitespace. */
const hasWhitespaceBetween = (current: WordSource, next: WordSource): boolean => {
  if (current.cue === next.cue && current.charEnd !== undefined && next.charStart !== undefined) {
    return next.charStart > current.charEnd;
  }
  // No exact spans (or a cue boundary): mirror the junction rule that
  // textFromSources uses when it rebuilds the text.
  return hasSpaceJunction(current.word.text.trim(), next.word.text.trim());
};

/**
 * Flatten cues to a word stream and regroup them with the legacy segmentation
 * rules: stop punctuation, max characters, max duration, and gaps.
 */
export function resegmentCues(
  cues: Cue[],
  config: Partial<CueSegmentationConfig> = {},
  options: CueSegmentationOptions = {}
): Cue[] {
  const mergedConfig = { ...DEFAULT_SEGMENTATION_CONFIG, ...config };
  const sources = cues.flatMap((cue) => sourcesForCue(cue));
  const usedPreservedIds = new Set<string>();
  const result: Cue[] = [];
  let buffer: WordSource[] = [];

  const flush = () => {
    const cue = cueFromSources(buffer, usedPreservedIds, options.createId);
    if (cue) result.push(cue);
    buffer = [];
  };

  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    if (!source) continue;
    const nextSource = sources[i + 1];

    if (
      wouldExceedMaxDuration(buffer, source.word, mergedConfig.maxDurationS) ||
      wouldExceedMaxChars(buffer, source, mergedConfig.maxChars)
    ) {
      flush();
    }

    buffer.push({ ...source, word: cloneWord(source.word) });

    if (
      endsWithStopCharacter(source.word, mergedConfig.stopCharacters) ||
      hasSplitAfterGap(source.word, nextSource?.word, mergedConfig.splitAfterGapS) ||
      (mergedConfig.splitOnWhitespace &&
        nextSource !== undefined &&
        hasWhitespaceBetween(source, nextSource))
    ) {
      flush();
    }
  }

  flush();
  return result;
}
